import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import { usePlatformAPI } from "@/api/PlatformProvider";
import { type APIError } from "@/api/APIError";
import { ErrorBox } from "@/components/ui/ErrorBox";
import { FieldSection, FieldButton, FieldSlider } from "@/components/ui/Field";
import { WarningBox } from "@/components/ui/WarningBox";
import { useGolemErrors } from "@/lib/golem/useGolemErrors";

type PartnerApp = {
    id: string;
    name: string;
    requireSigning: boolean;
    rollAfterDays?: number;
    rolloverDays?: number;
    codeLength: number;
    codeFormat: string;
};


const formatLabel = (codeFormat: string, codeLength: number) => {
    const fmt = codeFormat === "alphanumeric" ? "alphanumeric" : "numeric";
    return `${codeLength}-character ${fmt} code`;
};

function EditAppCredentialSettingsPage() {
    const { id, appId } = useParams<{ id: string; appId: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const api = usePlatformAPI();
    const appsFrom = encodeURIComponent(searchParams.get("from") ?? "/partners");
    const partnerName = searchParams.get("partnerName");
    const appName = searchParams.get("appName");
    const partnerNameParam = partnerName ? `&partnerName=${encodeURIComponent(partnerName)}` : "";
    const editURL = `/partners/${id}/apps/${appId}/edit?from=${appsFrom}${partnerNameParam}`;

    const [app, setApp] = useState<PartnerApp | null>(null);
    const [loadError, setLoadError] = useState<APIError | null>(null);

    const [requireSigning, setRequireSigning] = useState(false);
    const [rollAfterDays, setRollAfterDays] = useState("0");
    const [rolloverDays, setRolloverDays] = useState("7");
    const [submitting, setSubmitting] = useState(false);
    const [saveError, setSaveError] = useState<APIError | null>(null);
    const [saved, setSaved] = useState(false);
    const golemErrors = useGolemErrors();

    useEffect(() => {
        api.get<PartnerApp>(`api/partners/${id}/apps/${appId}`).then(([data, err]) => {
            if (err) { setLoadError(err); return; }
            setApp(data);
            setRequireSigning(data.requireSigning);
            setRollAfterDays(data.rollAfterDays?.toString() ?? "0");
            setRolloverDays(data.rolloverDays?.toString() ?? "7");
        });
    }, [api, id, appId]);

    const handleSave = async () => {
        setSaveError(null);
        setSaved(false);
        golemErrors.clearErrors();
        setSubmitting(true);
        const [, err] = await api.put(`api/partners/${id}/apps/${appId}/credential-settings`, {
            requireSigning,
            rollAfterDays: parseInt(rollAfterDays, 10) || null,
            rolloverDays: parseInt(rolloverDays, 10) || null,
        });
        setSubmitting(false);
        if (err) { setSaveError(err); return; }
        setSaved(true);
    };

    const breadcrumbAppName = appName || app?.name;

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-row items-center gap-4">
                <button onClick={() => navigate(editURL)} className="text-grey hover:text-dark-dark-grey flex items-center gap-1">
                    <ArrowBackIcon fontSize="small" />
                </button>
                <h1 className="flex items-center gap-2 text-xl">
                    <span className="text-grey font-medium">Apps</span>
                    {breadcrumbAppName && (
                        <>
                            <span className="text-light-grey">/</span>
                            <span className="text-grey font-medium">{breadcrumbAppName}</span>
                        </>
                    )}
                    <span className="text-light-grey">/</span>
                    <span className="font-semibold">Credential Settings</span>
                </h1>
            </div>
            <ErrorBox error={loadError} />
            {!loadError && !app && (
                <div className="flex justify-center py-12">
                    <LoadingSpinner width={32} height={32} />
                </div>
            )}
            {app && (
                <div className="bg-white shadow-md rounded-md p-6 flex flex-col gap-6">
                    <FieldSection title="Portal Code" first>
                        <div className="flex flex-col gap-1.5">
                            <span className="text-xs font-medium text-dark-grey uppercase tracking-wide">Format</span>
                            <p className="text-sm text-dark-dark-grey">{formatLabel(app.codeFormat, app.codeLength)}</p>
                        </div>
                    </FieldSection>

                    <FieldSection title="Request Signing">
                        <label className={`flex items-center gap-4 cursor-pointer rounded-lg border p-3.5 transition-all select-none ${
                            requireSigning
                                ? "border-blue/50 bg-blue/[0.04] ring-1 ring-blue/20"
                                : "border-light-grey hover:border-grey"
                        }`}>
                            <input type="checkbox" checked={requireSigning} onChange={e => setRequireSigning(e.target.checked)} className="sr-only" />
                            <div className="flex-1">
                                <p className={`text-sm font-medium ${requireSigning ? "text-blue" : "text-dark-dark-grey"}`}>
                                    Require request signing
                                </p>
                                <p className="text-xs text-grey leading-relaxed mt-0.5">
                                    API requests must include an Ed25519 signature, verifying they originate from the partner.
                                </p>
                            </div>
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                requireSigning ? "bg-blue border-blue" : "border-light-grey"
                            }`}>
                                {requireSigning && (
                                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 12 12" stroke="currentColor" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M2 6l3 3 5-5" />
                                    </svg>
                                )}
                            </div>
                        </label>
                    </FieldSection>

                    <FieldSection title="Credential Rolling">
                        <FieldSlider
                            label="Roll After"
                            value={parseInt(rollAfterDays)}
                            onChange={v => {
                                const clamped = v > 0 && v < 30 ? 30 : v;
                                setRollAfterDays(clamped.toString());
                                if (clamped > 0 && parseInt(rolloverDays) === 0) setRolloverDays("7");
                            }}
                            min={0} max={365}
                            formatValue={v => v === 0 ? "Disabled" : `${v} days`}
                            minLabel="Disabled" maxLabel="365d"
                        />
                        <FieldSlider
                            label="Rollover Window"
                            value={Math.max(parseInt(rolloverDays) || 7, 7)}
                            onChange={v => setRolloverDays(v.toString())}
                            min={7} max={60}
                            disabled={parseInt(rollAfterDays) === 0}
                            formatValue={v => parseInt(rollAfterDays) === 0 ? "Disabled" : `${v} days`}
                            minLabel="7d" maxLabel="60d"
                        />
                    </FieldSection>

                    <WarningBox>Saving these settings will trigger a credential reroll. Any currently active credentials will expire in 30 days.</WarningBox>

                    <ErrorBox error={saveError} />
                    {saved && (
                        <p className="text-xs text-dark-green">Credential settings saved.</p>
                    )}
                    <div className="flex justify-end">
                        <div className="w-48">
                            <FieldButton text="Save Credential Settings" loading={submitting} onClick={handleSave} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default EditAppCredentialSettingsPage;
